import React, { useState, useEffect, useRef } from 'react'
import { MenuProps } from 'components/menu'
import FloatingMenu from 'components/menu/floating'
import { MenuHeaderAction } from 'components/menu/items'
import { ITableColumn, ITableViewRowLock } from 'types'
import { IFilterType, IColumnTypes, ISelectOption } from 'components/spreadsheet/types'
import LockItem from 'components/spreadsheet/components/menu/views/lock/LockItem'
import { getOriginalColumns } from 'components/spreadsheet/helpers/functions'
import { areRowLockSettingsEqual } from 'components/spreadsheet/helpers/locking'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { PanelAdd, PanelRules } from 'components/menu/panel'
import IconSelector from 'components/spreadsheet/components/cell/components/icon'
import { cancelTimeout, requestTimeout, TimeoutID } from 'helpers/timer'

export interface ISelectAndKindOption extends ISelectOption {
  kind: IColumnTypes
  disabled?: boolean
  tooltip?: string
}
export interface ILockItemProps extends ITableViewRowLock {
  selectedColumns: ISelectOption[]
  kind: IColumnTypes
  column: ITableColumn
  columnOptions: ISelectAndKindOption[]
  focus: boolean
}

// `applyToEntireRow` distinguishes row-vs-cell scope; `value` / `multipleValues`
// can be `undefined` after a server round-trip for `all_values` rules
// (RowLockSchema strips them on dump), so coerce so two rules differing only
// by scope don't collapse to the same `-undefined-` key.
export const computeLockDedupeKey = (columnId: string, lock: ILockItemProps): string => {
  const rowScope = lock.applyToEntireRow ? '1' : '0'
  const ruleValue = lock.value ?? ''
  const ruleMultiple = JSON.stringify(lock.multipleValues ?? [])
  return `${columnId}-${lock.filterType}-${rowScope}-${ruleValue}-${ruleMultiple}`
}

const LockMenu: React.FC<MenuProps> = ({ id, menuState, setMenuState, width }) => {
  const { spreadsheetData, setSpreadsheetData } = useDataContext()
  const allColumns = getOriginalColumns(spreadsheetData)

  const columnOptions: ISelectAndKindOption[] = allColumns.map((column) => ({
    label: column.name,
    value: column.publicId,
    kind: column.kind,
    // Formula columns are already read-only, so locking them is a no-op — disable the option.
    disabled: column.formulaEnabled,
    tooltip: column.formulaEnabled ? 'Formula columns are automatically locked' : undefined
  }))

  const [rowLockSettings, setRowLockSettings] = useState<ILockItemProps[]>(() => {
    // Each rule renders as its own row — no grouping by identical settings.
    // This keeps "lock this column with this rule" 1:1 with the database row,
    // so adding a new column from "Column To Lock" or right-clicking always
    // produces a distinct entry the user can edit independently.
    const rows: ILockItemProps[] = []
    spreadsheetData.userConfiguration.rowLockSettings.forEach((lock) => {
      const column = allColumns.find((c) => c.publicId === lock.columnId)
      if (!column) return
      rows.push({
        ...lock,
        kind: column.kind,
        selectedColumns: [{ value: lock.columnId, label: lock.columnName }],
        column,
        columnOptions,
        focus: false
      })
    })
    return rows
  })

  // Debounce changes back to the global state, mirroring colour-menu cadence.
  const timeoutRef = useRef<TimeoutID | null>(null)
  useEffect(() => {
    const hasChanged = rowLockSettings !== spreadsheetData.userConfiguration.rowLockSettings
    if (spreadsheetData.loading || !hasChanged) {
      return
    }
    if (timeoutRef.current) {
      cancelTimeout(timeoutRef.current)
    }

    const keys = new Set()
    let newLockSettings = rowLockSettings.flatMap((lock) => {
      return lock.selectedColumns.map((col) => {
        const key = computeLockDedupeKey(col.value, lock)
        if (keys.has(key)) {
          return undefined
        } else {
          keys.add(key)
          const isMultiple = lock.filterType === IFilterType.one_of || lock.filterType === IFilterType.not_one_of
          const newLockSetting: ITableViewRowLock = {
            columnId: col.value,
            columnName: col.label,
            filterType: lock.filterType,
            value: isMultiple ? '' : lock.value || '',
            multipleValues: isMultiple ? lock.multipleValues || [] : [],
            applyToEntireRow: lock.applyToEntireRow
          }
          return newLockSetting
        }
      })
    })
    newLockSettings = newLockSettings.filter((c) => c !== undefined)
    const rebuiltLockSettings = newLockSettings as ITableViewRowLock[]
    if (areRowLockSettingsEqual(rebuiltLockSettings, spreadsheetData.userConfiguration.rowLockSettings)) {
      return
    }
    // The view it's made in: should the author have switched view by the time it saves, it's dropped.
    const viewId = spreadsheetData.viewDetails.publicId
    timeoutRef.current = requestTimeout(() => {
      setSpreadsheetData({
        type: 'UPDATE_LOCKS',
        locks: rebuiltLockSettings,
        viewId
      })
    }, 1000)
  }, [rowLockSettings, spreadsheetData.loading])

  const handleDeleteLock = (index: number) => {
    setRowLockSettings(rowLockSettings.filter((_, i) => i !== index))
  }

  // Each row is identified by its position in the array, not by its content.
  // Two rows with identical settings are still distinct rules, so we update by index.
  const handleLockChange = (newLock: ILockItemProps, prevLock: ILockItemProps, index: number) => {
    const newLockSettings = [...rowLockSettings]
    newLockSettings[index] = { ...newLock, focus: prevLock.value !== newLock.value }
    setRowLockSettings(newLockSettings)
  }
  function clearFocus(arr: ILockItemProps[]) {
    return arr.map((c) => ({ ...c, focus: false }))
  }

  const handleAddLock = (option: string, filterType?: IFilterType) => {
    const columnName = columnOptions.find((col) => col.value === option)?.label ?? ''
    const matchedColumn = allColumns.find((c) => c.publicId === option)
    if (!matchedColumn) return

    const newLock: ITableViewRowLock = {
      columnId: option,
      columnName: columnName,
      filterType: filterType ? filterType : IFilterType.all_values,
      value: '',
      multipleValues: [],
      applyToEntireRow: false
    }

    // Always create a new row when adding a column from "Column To Lock" so
    // the user can immediately differentiate it (e.g. set a value-based rule).
    // If they leave settings identical, the initial-state grouping on next
    // reload will collapse them visually.
    const column = allColumns.find((c) => c.publicId === option)
    if (!column) return
    const newLockSettings = clearFocus(rowLockSettings)
    newLockSettings.push({
      ...newLock,
      kind: column.kind,
      column,
      columnOptions,
      selectedColumns: [{ value: newLock.columnId, label: newLock.columnName }],
      focus: true
    })
    setRowLockSettings(newLockSettings)
  }

  return (
    <FloatingMenu
      id={id}
      menuState={menuState}
      setMenuState={setMenuState}
      width={width}
      zIndex={3000}
      title="Lock rows"
      action={
        rowLockSettings.length > 0 && (
          <MenuHeaderAction onClick={() => setRowLockSettings([])}>Clear all</MenuHeaderAction>
        )
      }
    >
      {rowLockSettings.length > 0 && (
        // Any rule a row meets locks it, so the order changes nothing and there's nothing to drag.
        <PanelRules ids={rowLockSettings.map((_, index) => `lock-${index}`)}>
          {rowLockSettings.map((lockSetting, index) => (
            <LockItem
              key={`lock-item-${index}`}
              {...lockSetting}
              onDelete={handleDeleteLock}
              onChange={(newLock, prevLock) => handleLockChange(newLock, prevLock, index)}
              index={index}
              optionsOpen={false}
            />
          ))}
        </PanelRules>
      )}
      <PanelAdd
        data-tour="add-lock"
        label="Add rule"
        options={columnOptions.map((option) => ({ ...option, icon: <IconSelector kind={option.kind} /> }))}
        onPick={(option) => handleAddLock(option)}
      />
    </FloatingMenu>
  )
}

export default LockMenu
